import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { UserPlus, Search, ChevronDown, ArrowUpDown } from 'lucide-react';
import {
    assignVerificationProcessesToProcessManager,
    getVerificationProcessAssignments,
    updateVerificationProcessAssignment,
    deleteVerificationProcessAssignment
} from '../actions/verificationAssignProcessAction';
import { getUsers } from '../actions/userActions';
import { getVerificationProcesses } from '../actions/verificationProcessActions';
import {
    ASSIGN_VERIFICATION_PROCESSES_TO_PROCESS_MANAGER_RESET,
    UPDATE_VERIFICATION_PROCESS_ASSIGNMENT_RESET,
    DELETE_VERIFICATION_PROCESS_ASSIGNMENT_RESET
} from '../constants/verificationAssignProcessConstant';

const AssignVerificationProcess = () => {
    const dispatch = useDispatch();

    // Redux state selectors
    const userLogin = useSelector((state) => state.userLogin);
    const { userInfo } = userLogin;

    const getUsersInfo = useSelector((state) => state.getUsersInfo);
    const { getUsersData } = getUsersInfo;

    const getVerificationProcessesInfo = useSelector((state) => state.getVerificationProcessesInfo);
    const { getVerificationProcessesData } = getVerificationProcessesInfo;

    const assignVerificationProcessesInfo = useSelector((state) => state.assignVerificationProcessesInfo);
    const { loadingAssignVerificationProcesses, errorAssignVerificationProcesses, assignVerificationProcessesData } = assignVerificationProcessesInfo;

    const verificationProcessAssignmentsInfo = useSelector((state) => state.verificationProcessAssignmentsInfo);
    const { loadingVerificationProcessAssignments, errorVerificationProcessAssignments, verificationProcessAssignmentsData } = verificationProcessAssignmentsInfo;

    const updateVerificationProcessAssignmentInfo = useSelector((state) => state.updateVerificationProcessAssignmentInfo);
    const { loadingUpdateVerificationProcessAssignment, errorUpdateVerificationProcessAssignment, updateVerificationProcessAssignmentData } = updateVerificationProcessAssignmentInfo;

    const deleteVerificationProcessAssignmentInfo = useSelector((state) => state.deleteVerificationProcessAssignmentInfo);
    const { loadingDeleteVerificationProcessAssignment, errorDeleteVerificationProcessAssignment, deleteVerificationProcessAssignmentData } = deleteVerificationProcessAssignmentInfo;

    // Local state
    const [showModal, setShowModal] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [sortField, setSortField] = useState('supervisor');
    const [sortDirection, setSortDirection] = useState('asc');
    const [currentPage, setCurrentPage] = useState(1);
    const [editingId, setEditingId] = useState(null);

    const [formData, setFormData] = useState({
        processManagerId: '',
        verificationProcessIds: []
    });

    // Get data from Redux state
    const processManagers = getUsersData?.users?.filter((u) => u.role?.name === 'Process Manager') || [];
    const allVerificationProcesses = Array.isArray(getVerificationProcessesData) ? getVerificationProcessesData : (getVerificationProcessesData?.data || []);
    const assignments = verificationProcessAssignmentsData?.data || [];
    const totalPages = verificationProcessAssignmentsData?.totalPages || 1;

    // Fetch initial data
    useEffect(() => {
        if (userInfo) {
            dispatch(getUsers());
            dispatch(getVerificationProcesses());
        }
    }, [dispatch, userInfo]);

    // Fetch assignments when search/page changes
    useEffect(() => {
        if (userInfo) {
            const params = new URLSearchParams({
                search: searchTerm,
                page: currentPage.toString(),
                limit: '10',
            });
            dispatch(getVerificationProcessAssignments(params.toString()));
        }
    }, [dispatch, userInfo, searchTerm, currentPage]);

    // Handle success actions
    useEffect(() => {
        if (assignVerificationProcessesData) {
            setShowModal(false);
            setFormData({ processManagerId: '', verificationProcessIds: [] });
            setEditingId(null);

            // Refresh assignments
            const params = new URLSearchParams({
                search: searchTerm,
                page: currentPage.toString(),
                limit: '10',
            });
            dispatch(getVerificationProcessAssignments(params.toString()));

            // Reset after 3 seconds
            setTimeout(() => {
                dispatch({ type: ASSIGN_VERIFICATION_PROCESSES_TO_PROCESS_MANAGER_RESET });
            }, 3000);
        }
    }, [assignVerificationProcessesData, dispatch, searchTerm, currentPage]);

    useEffect(() => {
        if (updateVerificationProcessAssignmentData) {
            setShowModal(false);
            setFormData({ processManagerId: '', verificationProcessIds: [] });
            setEditingId(null);

            // Refresh assignments
            const params = new URLSearchParams({
                search: searchTerm,
                page: currentPage.toString(),
                limit: '10',
            });
            dispatch(getVerificationProcessAssignments(params.toString()));

            // Reset after 3 seconds
            setTimeout(() => {
                dispatch({ type: UPDATE_VERIFICATION_PROCESS_ASSIGNMENT_RESET });
            }, 3000);
        }
    }, [updateVerificationProcessAssignmentData, dispatch, searchTerm, currentPage]);

    useEffect(() => {
        if (deleteVerificationProcessAssignmentData) {
            // Refresh assignments
            const params = new URLSearchParams({
                search: searchTerm,
                page: currentPage.toString(),
                limit: '10',
            });
            dispatch(getVerificationProcessAssignments(params.toString()));

            // Reset after 3 seconds
            setTimeout(() => {
                dispatch({ type: DELETE_VERIFICATION_PROCESS_ASSIGNMENT_RESET });
            }, 3000);
        }
    }, [deleteVerificationProcessAssignmentData, dispatch, searchTerm, currentPage]);

    const handleCheckbox = (id) => {
        setFormData((prev) => {
            const already = prev.verificationProcessIds.includes(id);
            return {
                ...prev,
                verificationProcessIds: already
                    ? prev.verificationProcessIds.filter((pid) => pid !== id)
                    : [...prev.verificationProcessIds, id],
            };
        });
    };

    const handleSubmit = (e) => {
        e.preventDefault();

        if (editingId) {
            dispatch(updateVerificationProcessAssignment(editingId, formData.verificationProcessIds));
        } else {
            dispatch(assignVerificationProcessesToProcessManager(formData.processManagerId, formData.verificationProcessIds));
        }
    };

    const handleEdit = (assignment) => {
        setFormData({
            processManagerId: assignment.supervisor?._id || '',
            verificationProcessIds: assignment.verificationProcesses.map(p => p._id),
        });
        setEditingId(assignment._id);
        setShowModal(true);
    };

    const handleDelete = (id) => {
        if (window.confirm('Are you sure you want to delete this assignment?')) {
            dispatch(deleteVerificationProcessAssignment(id));
        }
    };

    const handleSort = (field) => {
        if (sortField === field) {
            setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
        } else {
            setSortField(field);
            setSortDirection('asc');
        }
    };

    // Helper function to map assignment for sorting
    const getFieldValue = (assignment, field) => {
        if (field === 'supervisor') {
            return assignment.supervisor?.name || '';
        }
        if (field === 'verificationProcesses') {
            return assignment.verificationProcesses?.length || 0;
        }
        return '';
    };

    const sortedAssignments = [...assignments].sort((a, b) => {
        const fieldA = getFieldValue(a, sortField).toString().toLowerCase();
        const fieldB = getFieldValue(b, sortField).toString().toLowerCase();
        if (fieldA < fieldB) return sortDirection === 'asc' ? -1 : 1;
        if (fieldA > fieldB) return sortDirection === 'asc' ? 1 : -1;
        return 0;
    });

    const filteredAssignments = sortedAssignments.filter((assignment) => {
        const processManagerName = assignment.supervisor?.name || '';
        const processManagerEmail = assignment.supervisor?.email || '';
        const verificationProcessNames = assignment.verificationProcesses.map(p => p.process).join(' ');
        const lowerSearch = searchTerm.toLowerCase();

        return (
            processManagerName.toLowerCase().includes(lowerSearch) ||
            processManagerEmail.toLowerCase().includes(lowerSearch) ||
            verificationProcessNames.toLowerCase().includes(lowerSearch)
        );
    });

    const handleCloseModal = () => {
        setShowModal(false);
        setFormData({ processManagerId: '', verificationProcessIds: [] });
        setEditingId(null);

        // Reset error states
        dispatch({ type: ASSIGN_VERIFICATION_PROCESSES_TO_PROCESS_MANAGER_RESET });
        dispatch({ type: UPDATE_VERIFICATION_PROCESS_ASSIGNMENT_RESET });
    };

    return (
        <div className="p-6 bg-gray-50 min-h-screen">
            <div className="max-w-6xl mx-auto">
                {/* Success Messages */}
                {(assignVerificationProcessesData || updateVerificationProcessAssignmentData || deleteVerificationProcessAssignmentData) && (
                    <div className="mb-4 bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded">
                        {assignVerificationProcessesData && assignVerificationProcessesData.message}
                        {updateVerificationProcessAssignmentData && updateVerificationProcessAssignmentData.message}
                        {deleteVerificationProcessAssignmentData && deleteVerificationProcessAssignmentData.message}
                    </div>
                )}

                {/* Error Messages */}
                {(errorAssignVerificationProcesses || errorVerificationProcessAssignments || errorUpdateVerificationProcessAssignment || errorDeleteVerificationProcessAssignment) && (
                    <div className="mb-4 bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
                        {errorAssignVerificationProcesses || errorVerificationProcessAssignments || errorUpdateVerificationProcessAssignment || errorDeleteVerificationProcessAssignment}
                    </div>
                )}

                <div className="bg-white rounded-xl shadow-md overflow-hidden">
                    <div className="p-6 border-b border-gray-200">
                        <div className="flex justify-between items-center">
                            <h1 className="text-2xl font-bold text-gray-800">Assign Verification Processes </h1>
                            <button
                                onClick={() => {
                                    setEditingId(null);
                                    setFormData({ processManagerId: '', verificationProcessIds: [] });
                                    setShowModal(true);
                                }}
                                className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition flex items-center gap-2"
                            >
                                <UserPlus size={18} />
                                <span>Assign Verification Process</span>
                            </button>
                        </div>
                        <div className="mt-4 w-full bg-gray-100 p-3 rounded-lg shadow-sm">
                            <div className="relative">
                                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                    <Search size={18} className="text-gray-500" />
                                </div>
                                <input
                                    type="text"
                                    placeholder="Search by process manager or verification process..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    className="pl-10 w-full px-6 py-2 bg-gray-200 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                />
                            </div>
                        </div>
                    </div>

                    <div className="overflow-x-auto">
                        {loadingVerificationProcessAssignments ? (
                            <div className="p-6 text-center text-gray-500">Loading assignments...</div>
                        ) : (
                            <table className="min-w-full divide-y divide-gray-200">
                                <thead className="bg-gray-50">
                                    <tr>
                                        {['supervisor', 'verificationProcesses'].map((field) => (
                                            <th
                                                key={field}
                                                onClick={() => handleSort(field)}
                                                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                                            >
                                                <div className="flex items-center space-x-1">
                                                    <span>{field === 'supervisor' ? 'Process Manager' : 'Verification Processes'}</span>
                                                    <ArrowUpDown size={14} className="text-gray-400" />
                                                </div>
                                            </th>
                                        ))}
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                                    </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-200">
                                    {filteredAssignments.length > 0 ? (
                                        filteredAssignments.map((assignment) => (
                                            <tr key={assignment._id} className="hover:bg-gray-50 transition">
                                                <td className="px-6 py-4 whitespace-nowrap">
                                                    <div className="flex items-center">
                                                        <div className="flex-shrink-0 h-10 w-10 bg-purple-100 rounded-full flex items-center justify-center">
                                                            <span className="text-purple-600 font-medium">
                                                                {assignment.supervisor?.name ? assignment.supervisor.name.charAt(0).toUpperCase() : '?'}
                                                            </span>
                                                        </div>
                                                        <div className="ml-4">
                                                            <div className="text-sm font-medium text-gray-900">{assignment.supervisor?.name}</div>
                                                            <div className="text-sm text-gray-500">{assignment.supervisor?.email}</div>
                                                        </div>
                                                    </div>
                                                </td>
                                                <td className="px-6 py-4">
                                                    <div className="flex flex-wrap gap-1">
                                                        {assignment.verificationProcesses.map((verificationProcess) => (
                                                            <span
                                                                key={verificationProcess._id}
                                                                className="inline-block bg-purple-100 text-purple-800 px-2 py-1 rounded-full text-xs"
                                                            >
                                                                {verificationProcess.process}
                                                            </span>
                                                        ))}
                                                    </div>
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm">
                                                    <button
                                                        className="text-blue-600 hover:text-blue-900 mr-3 disabled:opacity-50"
                                                        onClick={() => handleEdit(assignment)}
                                                        disabled={loadingUpdateVerificationProcessAssignment}
                                                    >
                                                        Edit
                                                    </button>
                                                    <button
                                                        className="text-red-600 hover:text-red-900 disabled:opacity-50"
                                                        onClick={() => handleDelete(assignment._id)}
                                                        disabled={loadingDeleteVerificationProcessAssignment}
                                                    >
                                                        Delete
                                                    </button>
                                                </td>
                                            </tr>
                                        ))
                                    ) : (
                                        <tr>
                                            <td colSpan="3" className="px-6 py-12 text-center">
                                                <div className="text-gray-500">No assignments found</div>
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        )}

                        {filteredAssignments.length > 0 && (
                            <div className="px-6 py-3 bg-gray-50 border-t border-gray-200 text-xs text-gray-600">
                                Showing {filteredAssignments.length} of {assignments.length} assignments
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Pagination Controls */}
            {totalPages > 1 && (
                <div className="flex justify-center items-center space-x-2 mt-4">
                    <button
                        onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                        disabled={currentPage === 1}
                        className={`px-3 py-1 rounded border ${currentPage === 1 ? 'bg-gray-200 cursor-not-allowed' : 'bg-white hover:bg-gray-100'
                            }`}
                    >
                        Prev
                    </button>

                    {[...Array(totalPages)].map((_, i) => {
                        const page = i + 1;
                        return (
                            <button
                                key={page}
                                onClick={() => setCurrentPage(page)}
                                className={`px-3 py-1 rounded border ${page === currentPage ? 'bg-blue-600 text-white' : 'bg-white hover:bg-gray-100'
                                    }`}
                            >
                                {page}
                            </button>
                        );
                    })}

                    <button
                        onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
                        disabled={currentPage === totalPages}
                        className={`px-3 py-1 rounded border ${currentPage === totalPages ? 'bg-gray-200 cursor-not-allowed' : 'bg-white hover:bg-gray-100'
                            }`}
                    >
                        Next
                    </button>
                </div>
            )}

            {/* Modal */}
            {showModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-xl shadow-xl p-6 w-full max-w-lg relative animate-fadeIn">
                        <button
                            onClick={handleCloseModal}
                            className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
                        >
                            <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>

                        <h2 className="text-2xl font-bold mb-6 text-center text-gray-800">
                            {editingId ? 'Edit Assignment' : 'Assign Verification Processes '}
                        </h2>

                        {(errorAssignVerificationProcesses || errorUpdateVerificationProcessAssignment) && (
                            <div className="mb-4 text-sm text-red-700 bg-red-50 p-3 rounded-md border border-red-200">
                                {errorAssignVerificationProcesses || errorUpdateVerificationProcessAssignment}
                            </div>
                        )}

                        <div>
                            <div className="mb-4">
                                <label className="block mb-2 text-sm font-medium text-gray-700">Process Manager</label>
                                <div className="relative">
                                    <select
                                        value={formData.processManagerId}
                                        onChange={(e) => setFormData({ ...formData, processManagerId: e.target.value })}
                                        required
                                        className="w-full px-4 py-2 border border-gray-300 rounded-lg appearance-none"
                                        disabled={editingId} // Disable when editing
                                    >
                                        <option value="">Select Process Manager</option>
                                        {processManagers.map((processManager) => (
                                            <option key={processManager._id} value={processManager._id}>
                                                {processManager.name} ({processManager.email})
                                            </option>
                                        ))}
                                    </select>
                                    <div className="absolute inset-y-0 right-0 flex items-center px-2 pointer-events-none">
                                        <ChevronDown size={18} className="text-gray-500" />
                                    </div>
                                </div>
                            </div>

                            <div className="mb-6">
                                <label className="block mb-2 text-sm font-medium text-gray-700">Select Verification Processes</label>
                                <div className="max-h-48 overflow-y-auto border border-gray-300 rounded-lg p-3 space-y-2">
                                    {allVerificationProcesses.map((verificationProcess) => (
                                        <div key={verificationProcess._id} className="flex items-center">
                                            <input
                                                type="checkbox"
                                                id={`verificationProcess-${verificationProcess._id}`}
                                                checked={formData.verificationProcessIds.includes(verificationProcess._id)}
                                                onChange={() => handleCheckbox(verificationProcess._id)}
                                                className="mr-2 h-4 w-4 text-purple-600"
                                            />
                                            <label htmlFor={`verificationProcess-${verificationProcess._id}`} className="text-sm text-gray-700">
                                                {verificationProcess.process}
                                            </label>
                                        </div>
                                    ))}
                                    {allVerificationProcesses.length === 0 && (
                                        <div className="text-gray-500 text-sm">No verification processes available</div>
                                    )}
                                </div>
                            </div>

                            <button
                                onClick={handleSubmit}
                                disabled={loadingAssignVerificationProcesses || loadingUpdateVerificationProcessAssignment}
                                className={`w-full py-3 rounded-lg font-medium transition ${(loadingAssignVerificationProcesses || loadingUpdateVerificationProcessAssignment)
                                    ? 'bg-gray-400 cursor-not-allowed'
                                    : 'bg-purple-600 hover:bg-purple-700'
                                    } text-white`}
                            >
                                {(loadingAssignVerificationProcesses || loadingUpdateVerificationProcessAssignment)
                                    ? 'Processing...'
                                    : editingId
                                        ? 'Update Assignment'
                                        : 'Assign Verification Processes'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AssignVerificationProcess;